class QuickLinks extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['data-reset']
  }

  constructor () {
    super()

    switch (this.dataset.type) {
      case 'search':
        this.eventBus = window.theme.searchResults.eventBus
        this.state = {
          root: window.theme.searchResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }
        break
      case 'autocomplete':
        this.eventBus = window.theme.autocompleteResults.eventBus
        this.state = {
          root: window.theme.autocompleteResults?.stateLayer?.state || {
            products: [],
          },
          observer: null,
        }
        break
      default:
        // Default is collection
        this.eventBus = window.theme.category.eventBus
        this.state = {
          root: window.theme.category?.stateLayer?.state || { products: [] },
          observer: null,
        }
    }

    this.matchedMakers = []
  }

  connectedCallback () {
    this.subscribeToEvents()
    this.populateQuickLinks()

    this.updateFades = this.updateFades.bind(this)

    this.els.list.element.addEventListener('scroll', this.updateFades)
    window.addEventListener('resize', this.updateFades)
    this.els.arrowRight.element.addEventListener('click', () => {
      this.els.list.element.scrollBy({
        left: 200,
        behavior: 'smooth',
      })
    })

    this.els.arrowLeft.element.addEventListener('click', () => {
      this.els.list.element.scrollBy({
        left: -200,
        behavior: 'smooth',
      })
    })
  }

  attributeChangedCallback (name, oldValue, newValue) {
    if (name !== 'data-reset') return
    if (oldValue === newValue) return
    if (newValue !== 'true') return

    this.els.list.element.scrollLeft = 0
    this.updateFades()
    this.removeAttribute('data-reset')
  }

  subscribeToEvents () {
    if (this.dataset.type === 'autocomplete') {
      // For autocomplete, render once after both makers and suggestions are resolved.
      // api:makers:fetched is published last, so by the time state:matchedMakers:updated
      // fires, suggestions are already updated in state.
      this.eventBus.subscribe('state:matchedMakers:updated', makers => {
        this.matchedMakers = makers
        this.populateQuickLinks()
        this.quickLinksListener()
        this.updateFades()
      })
    } else {
      this.eventBus.subscribe('state:suggestions:updated', () => {
        this.populateQuickLinks()
        this.quickLinksListener()
        this.updateFades()
      })
    }
  }

  quickLinksListener () {
    this.els.list.element
      .querySelectorAll('button[data-suggest]')
      .forEach(button => {
        button.addEventListener('click', e => {
          const searchTerm = e.currentTarget.dataset.suggest

          // Clear sessionStorage to trigger filter reset on new search
          if (this.dataset.type === 'autocomplete') {
            sessionStorage.removeItem('currentSearchTerm')

            // Clear filters and update URL
            const url = new URL(window.location.href)
            url.searchParams.delete('search_filters')
            url.searchParams.set('q', searchTerm)
            window.history.pushState(
              { path: url.toString() },
              '',
              url.toString(),
            )

            // Reset filters in state and publish event to update UI
            if (window.theme.autocompleteResults?.stateLayer) {
              window.theme.autocompleteResults.stateLayer.activeFacets = []
            }

            // Close all open facet summaries
            const facetSummaries = document.querySelectorAll(
              'details[is="facet-summary"][open]',
            )
            facetSummaries.forEach(summary => {
              if (summary.close) {
                summary.close()
              } else {
                summary.removeAttribute('open')
              }
            })
          }

          this.externalEls.search.element.value = searchTerm
          this.externalEls.search.element.setAttribute(
            'data-quicklink-input',
            'true',
          )
          const headerSearch = this.closest('header-search')
          if (headerSearch) {
            headerSearch.setAttribute('data-quicklink-input', 'true')
          }
          this.externalEls.search.element.dispatchEvent(
            new Event('input', { bubbles: true }),
          )
          requestAnimationFrame(() => {
            this.externalEls.search.element.removeAttribute(
              'data-quicklink-input',
            )
          })
        })
      })
  }

  populateQuickLinks () {
    const emptyQuicklinksClass = 'clc-ProductGrid_HeaderContainer-noQuickLinks'
    const searchHeader = this.closest(
      '[data-header-scroll-external-el="searchHeader"]',
    )

    if (this.dataset.type === 'autocomplete') {
      this.populateAutocompleteList(searchHeader, emptyQuicklinksClass)
    } else {
      this.populateDefaultList(searchHeader, emptyQuicklinksClass)
    }
  }

  populateAutocompleteList (searchHeader, emptyQuicklinksClass) {
    const matchedMakers = this.matchedMakers || []

    const makerTitles = new Set(
      (window.theme.autocompleteResults.makersList || []).map(m =>
        m.title.toLowerCase(),
      ),
    )

    const suggestions = Array.isArray(this.state.root.suggestions)
      ? this.state.root.suggestions.filter(item => {
        const text = item.suggest.replace(/(<([^>]+)>)/gi, '')
        return (
          text !== window.theme.autocompleteResults.searchTerm &&
            !makerTitles.has(text.toLowerCase())
        )
      })
      : []

    const quickLinksLimit =
      Number(window.theme.collection.quickLinksLimit) || 20
    const trimmedSuggestions = suggestions.slice(
      0,
      quickLinksLimit - matchedMakers.length,
    )

    const hasMakers = matchedMakers.length > 0
    const hasSuggestions = trimmedSuggestions.length > 0

    if (!hasMakers && !hasSuggestions) {
      this.els.list.element.innerHTML = ''
      this.ariaHidden = true
      if (searchHeader) {
        searchHeader.classList.add(emptyQuicklinksClass)
      }
      return
    }

    const makersLabel = this.dataset.makersLabel || 'Makers'
    const suggestionsLabel = this.dataset.suggestionsLabel || 'Suggestions'

    let html = ''

    if (hasMakers) {
      const rawTerm = window.theme.autocompleteResults.searchTerm || ''

      const makersIcon =
        this.dataset.showMakersIcon === 'true'
          ? `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13.1829 0.795112L13.4922 13.4664L12.4923 13.4913L12.212 1.97731L1.8595 12.3298L1.1524 11.6227L11.4966 1.27849L-0.000103914 0.998819L0.0247553 -0.00107367L12.6954 0.307595L13.1711 0.319334L13.1829 0.795112Z" fill="currentColor"/>
        </svg>
      `
          : ''

      html += `<span class="clc-ProductGrid_QuickLinkLabel fz-15_35">${makersLabel}</span>`
      html += matchedMakers
        .map(maker => {
          const title = rawTerm
            ? maker.title.replace(
              new RegExp(
                  `(${rawTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
                  'gi',
              ),
              '<b>$1</b>',
            )
            : maker.title
          return `<a href="${maker.url}" class="clc-ProductGrid_QuickLink clc-ProductGrid_QuickLink-maker fz-15_35"><span>${title}</span>${makersIcon}</a>`
        })
        .join('')
    }

    if (hasMakers && hasSuggestions) {
      html += `<span class="clc-ProductGrid_QuickLinksSeparator"></span>`
    }

    if (hasSuggestions) {
      html += `<span class="clc-ProductGrid_QuickLinkLabel fz-15_35">${suggestionsLabel}</span>`
      html += trimmedSuggestions
        .map(
          obj =>
            `<button class="clc-ProductGrid_QuickLink fz-15_35" type="button" data-suggest="${obj.suggest.replace(
              /(<([^>]+)>)/gi,
              '',
            )}">
              ${obj.suggest}
            </button>`,
        )
        .join('')
    }

    this.els.list.element.innerHTML = html
    this.els.list.element.scrollLeft = 0

    this.ariaHidden = false

    if (searchHeader) {
      searchHeader.classList.remove(emptyQuicklinksClass)
    }
  }

  populateDefaultList (searchHeader, emptyQuicklinksClass) {
    const hasQuickLinks = Array.isArray(this.state.root.suggestions)

    if (!hasQuickLinks) {
      this.innerHTML = ''
      this.ariaHidden = true
      if (searchHeader) {
        searchHeader.classList.add(emptyQuicklinksClass)
      }
      return
    }

    const suggestions = this.state.root.suggestions.filter(
      item =>
        item.suggest.replace(/(<([^>]+)>)/gi, '') !==
        window.theme.autocompleteResults.searchTerm,
    )

    if (suggestions.length === 0) {
      this.ariaHidden = true
      if (searchHeader) {
        searchHeader.classList.add(emptyQuicklinksClass)
      }
      return
    }

    this.els.list.element.innerHTML = suggestions
      .map(
        obj => `<button class="clc-ProductGrid_QuickLink fz-15_35" type="button" data-suggest="${obj.suggest.replace(
          /(<([^>]+)>)/gi,
          '',
        )}">
              ${obj.suggest}
            </button>`,
      )
      .join('')

    this.ariaHidden = false
    if (searchHeader) {
      searchHeader.classList.remove(emptyQuicklinksClass)
    }
  }

  updateFades () {
    const atStart = this.els.list.element.scrollLeft <= 1
    const atEnd =
      this.els.list.element.scrollLeft + this.els.list.element.clientWidth >=
      this.els.list.element.scrollWidth - 1

    this.classList.toggle('no-left', atStart)
    this.classList.toggle('no-right', atEnd)
  }
}

if (!customElements.get('quick-links')) {
  customElements.define('quick-links', QuickLinks)
}
